import { Link } from '@tanstack/react-router';
import LockUp from '../assets/brand/lock-up.svg';
import Logo from '../assets/brand/logo.svg';
import { ReactElement } from 'react';
import Grid from '../assets/icons/layout-grid-01.svg';
import MessageChatCircle from '../assets/icons/message-chat-circle.svg';
import Globe from '../assets/icons/globe-01.svg';
import Palette from '../assets/icons/palette.svg';
import CurrencyPound from '../assets/icons/currency-pound.svg';
import Users from '../assets/icons/users-02.svg';
import SpeakerPhone from '../assets/icons/speaker-phone.svg';
import env from '../environment';
import { Tooltip } from 'react-tooltip';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';

export const Sidebar = ({
  collapsed,
}: {
  collapsed: boolean;
}): ReactElement => {
  const { setAlertModal } = useAlerts();

  const { isAdmin } = useUser();

  return (
    <div
      data-collapsed={collapsed}
      className="flex flex-col pt-10 px-3 pb-6 bg-background-primary group transition-all duration-150 border-r border-grey-t-800 data-[collapsed=true]:w-[65px] data-[collapsed=false]:w-[230px] data-[collapsed=true]:overflow-x-hidden group"
    >
      <Link to="/">
        {!collapsed ? (
          <LockUp className="h-5 mb-10" />
        ) : (
          <Logo className="h-5 mb-10" />
        )}
      </Link>
      <div className="flex flex-col justify-between grow">
        <div className="space-y-2">
          <Link
            to="/"
            className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
            {...(collapsed && {
              'data-tooltip-content': 'Dashboard',
              'data-tooltip-place': 'right',
              'data-tooltip-id': 'sidebar-dashboard',
              'data-tooltip-delay-show': 350,
            })}
          >
            <Grid className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
            <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
              Dashboard
            </span>
          </Link>
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-dashboard"
            />
          )}
          <Link
            to="/kb"
            className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
            {...(collapsed && {
              'data-tooltip-content': 'Knowledge bases',
              'data-tooltip-place': 'right',
              'data-tooltip-id': 'sidebar-projects',
              'data-tooltip-delay-show': 350,
            })}
          >
            <MessageChatCircle className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
            <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
              Knowledge bases
            </span>
          </Link>
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-projects"
            />
          )}
          <Link
            to="/domains"
            className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
            {...(collapsed && {
              'data-tooltip-content': 'Domains',
              'data-tooltip-place': 'right',
              'data-tooltip-id': 'sidebar-domains',
              'data-tooltip-delay-show': 350,
            })}
          >
            <Globe className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
            <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
              Domains
            </span>
          </Link>
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-domains"
            />
          )}
          <Link
            to="/brand-themes"
            className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
            {...(collapsed && {
              'data-tooltip-content': 'Brand themes',
              'data-tooltip-place': 'right',
              'data-tooltip-id': 'sidebar-brand-themes',
              'data-tooltip-delay-show': 350,
            })}
          >
            <Palette className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
            <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
              Brand themes
            </span>
          </Link>
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-brand-themes"
            />
          )}
        </div>
        <div className="space-y-2">
          {!collapsed && (
            <div className="p-2">
              <span className="text-section-title font-grotesque font-bold text-grey-400">
                ADMINISTRATION
              </span>
            </div>
          )}
          {isAdmin && (
            <>
              <Link
                to="/billing"
                className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
                {...(collapsed && {
                  'data-tooltip-content': 'Billing',
                  'data-tooltip-place': 'right',
                  'data-tooltip-id': 'sidebar-billing',
                  'data-tooltip-delay-show': 350,
                })}
              >
                <CurrencyPound className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
                  Billing
                </span>
              </Link>
              {collapsed && (
                <Tooltip
                  openEvents={{ mouseover: true }}
                  closeEvents={{ click: true, mouseleave: true, blur: true }}
                  className="z-20"
                  id="sidebar-billing"
                />
              )}
              <Link
                to="/users"
                className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group"
                {...(collapsed && {
                  'data-tooltip-content': 'Users',
                  'data-tooltip-place': 'right',
                  'data-tooltip-id': 'sidebar-users',
                  'data-tooltip-delay-show': 350,
                })}
              >
                <Users className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
                <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold group-data-[status=active]:text-primary-300">
                  Users
                </span>
              </Link>
            </>
          )}
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-users"
            />
          )}
          <button
            type="button"
            onClick={() =>
              setAlertModal({
                open: true,
                title: 'You will be redirected',
                description:
                  'You are about to be redirected to User Jot our feebdack partner.',
                icon: 'info',
                submitText: 'Go now',
                linkProps: {
                  href: 'https://starkchat.userjot.com',
                },
              })
            }
            className="data-[status=active]:bg-primary-t-600/10 flex items-center px-2 py-2.5 rounded-xs hover:bg-grey-t-900 active:shadow-focused-dark not-group-data-[collapsed=true]:space-x-2 group w-full"
            {...(collapsed && {
              'data-tooltip-content': 'Submit feedback',
              'data-tooltip-place': 'right',
              'data-tooltip-id': 'sidebar-submit-feedback',
              'data-tooltip-delay-show': 350,
            })}
          >
            <SpeakerPhone className="size-5 group-data-[status=active]:text-primary-400 text-grey-t-400 shrink-0" />
            <span className="group-data-[collapsed=true]:w-0 group-data-[collapsed=true]:opacity-0 overflow-hidden whitespace-nowrap transition-opacity text-body-medium font-semibold">
              Submit feedback
            </span>
          </button>
          {collapsed && (
            <Tooltip
              openEvents={{ mouseover: true }}
              closeEvents={{ click: true, mouseleave: true, blur: true }}
              className="z-20"
              id="sidebar-submit-feedback"
            />
          )}
        </div>
      </div>
      {!collapsed && (
        <>
          <hr className="h-px my-5 border-none bg-grey-t-700" />
          <div className="flex items-center justify-between">
            <small className="text-body-smallest">We're in BETA</small>
            <small className="text-body-smallest">v{env.appVersion}</small>
          </div>
        </>
      )}
    </div>
  );
};
